import { MUSIC_MUSEUM_SECTION_LAYOUTS, MUSIC_MUSEUM_SEAT_PLANS } from "../seatPlans/musicMuseumSeatPlans";
import {
  countSeatsForAreas,
  getSeatAreaOptions,
} from "./venueLayout";

const FALLBACK_VENUE_CONFIG = {
  rows: ["J", "I", "H", "G", "F", "E", "D", "C", "B", "A"],
  cols: Array.from({ length: 12 }, (_, index) => 12 - index),
  sections: [],
};

export const VENUE_SEAT_CONFIGS = {
  "Music Museum": {
    rows: ["J", "I", "H", "G", "F", "E", "D", "C", "B", "A"],
    cols: Array.from({ length: 12 }, (_, index) => 12 - index),
    sections: ["Orchestra A", "Orchestra H", "VIP", "Balcony"],
  },
  Teatrino: {
    rows: ["H", "G", "F", "E", "D", "C", "B", "A"],
    cols: Array.from({ length: 10 }, (_, index) => 10 - index),
    sections: ["Stalls", "Mezzanine", "Balcony"],
  },
  "NewPort Performing Arts Theater": {
    rows: ["K", "J", "I", "H", "G", "F", "E", "D", "C", "B", "A"],
    cols: Array.from({ length: 14 }, (_, index) => 14 - index),
    sections: ["Orchestra", "Mezzanine", "Balcony"],
  },
};

export const normalizeSeatAreas = (tier) => {
  const raw = tier?.seat_areas ?? tier?.seatAreas;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  return [];
};

export const parseLegacyTierSection = (sectionValue = "") => {
  const text = String(sectionValue || "").trim();
  if (!text.includes(" / ")) {
    return { section: text, seatAreas: [] };
  }

  const [parent, ...rest] = text.split(" / ");
  return {
    section: parent.trim(),
    seatAreas: rest.length ? [rest.join(" / ").trim()] : [],
  };
};

export const filterPagesBySeatAreas = (pages = [], seatAreas = []) => {
  if (!seatAreas?.length) return pages || [];
  const allowed = new Set(seatAreas.map(String));
  return (pages || []).filter((page) => allowed.has(String(page.label)));
};

export const parseSeatLayoutSnapshot = (raw) => {
  if (!raw) return null;

  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  if (!data || !Array.isArray(data.sections) || data.sections.length === 0) {
    return null;
  }

  return data;
};

export const hasSeatLayoutSnapshot = (event) => Boolean(parseSeatLayoutSnapshot(event?.seat_layout_snapshot));

export const shouldUseLegacySeatPlans = (event) =>
  !event?.venue_id && !hasSeatLayoutSnapshot(event);

const resolveMusicMuseumLayoutKey = (tier) => {
  if (!tier) return null;

  const directKey = tier.section || tier.name;
  if (directKey && MUSIC_MUSEUM_SECTION_LAYOUTS[directKey]) {
    return directKey;
  }

  const normalized = String(directKey || "").toLowerCase();
  if (normalized.includes("orchestra")) return "Orchestra";
  if (normalized.includes("vip")) return "VIP";
  if (normalized.includes("balcony")) return "Balcony";

  return null;
};

const applySeatAreaFilter = (layout, tier) => {
  if (!layout?.pages?.length) return layout;
  const seatAreas = normalizeSeatAreas(tier);
  if (!seatAreas.length) return layout;

  const pages = filterPagesBySeatAreas(layout.pages, seatAreas);
  if (!pages.length) return layout;

  return {
    ...layout,
    pages,
  };
};

export const findSnapshotSectionLayout = (snapshot, tier) => {
  const key = String(tier?.section || tier?.name || "").trim();
  if (!key || !snapshot?.sections) return null;

  for (const section of snapshot.sections) {
    const pages = Array.isArray(section.pages) ? section.pages : [];
    const compositeHits = pages.filter((page) => `${section.name} / ${page.label}` === key);
    if (compositeHits.length) {
      return applySeatAreaFilter(
        {
          label: section.name,
          pages: compositeHits,
          description: section.description || "",
          color: section.color || compositeHits[0]?.color || "",
        },
        tier
      );
    }

    const labelHits = pages.filter((page) => page.label === key);
    if (labelHits.length === 1) {
      return applySeatAreaFilter(
        {
          label: section.name,
          pages: labelHits,
          description: section.description || "",
          color: section.color || labelHits[0]?.color || "",
        },
        tier
      );
    }

    if (section.name === key && pages.length) {
      return applySeatAreaFilter(
        {
          label: section.name,
          pages,
          description: section.description || "",
          color: section.color || pages[0]?.color || "",
        },
        tier
      );
    }
  }

  return null;
};

const toRenderableLayout = ({ source, useCanonicalLabels, layout }) => {
  const pages = layout?.pages || null;
  const currentLayout = layout
    ? {
        label: layout.label,
        rows: layout.rows,
        rowSeatCounts: layout.rowSeatCounts,
        pages,
        description: layout.description,
        color: layout.color || pages?.[0]?.color || "",
      }
    : null;

  return {
    source,
    useCanonicalLabels,
    currentSectionLayout: currentLayout,
  };
};

export const resolveEventSectionLayout = ({ event, tier } = {}) => {
  const snapshot = parseSeatLayoutSnapshot(event?.seat_layout_snapshot);
  if (snapshot) {
    const snapshotLayout = findSnapshotSectionLayout(snapshot, tier);
    return toRenderableLayout({
      source: "snapshot",
      useCanonicalLabels: true,
      layout: snapshotLayout,
    });
  }

  const venueName = event?.venue;
  const layoutKey = venueName === "Music Museum" ? resolveMusicMuseumLayoutKey(tier) : null;
  const musicLayout = layoutKey ? MUSIC_MUSEUM_SECTION_LAYOUTS[layoutKey] : null;

  return toRenderableLayout({
    source: "legacy",
    useCanonicalLabels: false,
    layout: musicLayout
      ? applySeatAreaFilter(
          { ...musicLayout, label: musicLayout.label || layoutKey },
          tier
        )
      : null,
  });
};

export const getVenueSeatConfig = (venueName) =>
  VENUE_SEAT_CONFIGS[venueName] || FALLBACK_VENUE_CONFIG;

export const buildPickerSeatId = ({ useCanonicalLabels, page, row, column }) => {
  if (useCanonicalLabels) return `${row}${column}`;
  return `${page?.id ? `${page.id}-` : ""}${row}${column}`;
};

export const countEnabledSeatsInPages = (pages = []) =>
  (pages || []).reduce((total, page) => {
    const layout = page?.layout || [];
    return (
      total +
      layout.reduce(
        (rowTotal, row) => rowTotal + (row || []).filter(Boolean).length,
        0
      )
    );
  }, 0);

export const getMusicMuseumSeatAreaOptions = (parentName) => {
  const plan = MUSIC_MUSEUM_SEAT_PLANS[parentName];
  if (!plan?.pages?.length) return [];

  return plan.pages.map((page) => ({
    value: page.label,
    label: page.label,
  }));
};

export const countMusicMuseumSeatsForAreas = (parentName, areaLabels = []) => {
  const plan = MUSIC_MUSEUM_SEAT_PLANS[parentName];
  if (!plan?.pages?.length) return 0;

  const pages = areaLabels.length
    ? filterPagesBySeatAreas(plan.pages, areaLabels)
    : plan.pages;

  return countEnabledSeatsInPages(pages);
};

export const buildMusicMuseumPreviewPlan = (parentName, seatAreas = []) => {
  const plan = MUSIC_MUSEUM_SEAT_PLANS[parentName];
  if (!plan) return null;

  const pages = seatAreas.length
    ? filterPagesBySeatAreas(plan.pages || [], seatAreas)
    : plan.pages;

  if (!pages?.length) return null;

  return {
    ...plan,
    pages,
    canonical: false,
  };
};

export const calculateTierSeatQuantity = ({
  tier,
  venueLayout,
  snapshot,
  venueName,
} = {}) => {
  const parent = String(tier?.section || "").trim();
  const areas = normalizeSeatAreas(tier);
  if (!parent) return 0;

  if (venueLayout?.sections?.length) {
    const options = getSeatAreaOptions(venueLayout.sections, parent);
    if (options.length) {
      return countSeatsForAreas(venueLayout.sections, venueLayout.seats || [], parent, areas);
    }
  }

  if (snapshot) {
    const layout = findSnapshotSectionLayout(snapshot, { section: parent, seat_areas: areas });
    return countEnabledSeatsInPages(layout?.pages || []);
  }

  if (venueName === "Music Museum" || MUSIC_MUSEUM_SEAT_PLANS[parent]) {
    const options = getMusicMuseumSeatAreaOptions(parent);
    if (options.length) {
      return countMusicMuseumSeatsForAreas(parent, areas);
    }
  }

  return parseInt(tier?.quantity || 0, 10) || 0;
};

export const getTierSeatAreaOptions = ({ tier, venueLayout, venueName } = {}) => {
  const parent = String(tier?.section || "").trim();
  if (!parent) return [];

  if (venueLayout?.sections?.length) {
    const options = getSeatAreaOptions(venueLayout.sections, parent);
    if (options.length) return options;
  }

  if (venueName === "Music Museum" || MUSIC_MUSEUM_SEAT_PLANS[parent]) {
    return getMusicMuseumSeatAreaOptions(parent);
  }

  return [];
};

export const tierUsesAutoQuantity = ({ tier, venueLayout, venueName } = {}) =>
  getTierSeatAreaOptions({ tier, venueLayout, venueName }).length > 0;
